/**
 * @packageDocumentation
 * @module HackerAPI.Claim
 */
import { __extends } from "tslib";
import HackerAPIFetch from "../hackerapi-fetch/hackerapi-fetch";
import Collaborator from "./collaborator/collaborator";
import Review from "./review/review";
import Base from "../base/base";
import Filters from "./filters/filters";
import User from "../user/user";
import FieldAnswer from "./field-answer/field-answer";
import { parseDate } from "../utils/utils";
/**
 * A container for claim-related data and static methods for interfacing with hackerapi.com
 * @class
 */
var Claim = /** @class */ (function (_super) {
    __extends(Claim, _super);
    /**
     * Creates a new Claim instance
     * @constructor
     * @param claim
     */
    function Claim(_a) {
        var _b = _a === void 0 ? {} : _a, id = _b.id, name = _b.name, pipeline_id = _b.pipeline_id, pipelineId = _b.pipelineId, pipeline_slug = _b.pipeline_slug, pipelineSlug = _b.pipelineSlug, event_slug = _b.event_slug, eventSlug = _b.eventSlug, user_id = _b.user_id, userId = _b.userId, stage_id = _b.stage_id, stageId = _b.stageId, user = _b.user, stage = _b.stage, collaborators = _b.collaborators, reviews = _b.reviews, fields = _b.fields, created_at = _b.created_at, createdAt = _b.createdAt, updated_at = _b.updated_at, updatedAt = _b.updatedAt;
        var _this = _super.call(this) || this;
        /**
         * The claim ID
         * @type { string }
         */
        _this.id = id;
        /**
         * The name of the user whomst this claim is for
         * @type { string }
         */
        _this.name = name;
        /**
         * The ID of the pipeline that this claim is for
         * @type { number }
         */
        _this.pipelineId = pipeline_id || pipelineId;
        /**
         * The slug of the pipeline that this claim is for
         * @type { string }
         */
        _this.pipelineSlug = pipeline_slug || pipelineSlug;
        /**
         * The slug of the event that this claim is for
         * @type { string }
         */
        _this.eventSlug = event_slug || eventSlug;
        /**
         * The ID of the user who's claim this is
         * @type { number }
         */
        _this.userId = user_id || userId;
        /**
         * The stage ID of this claim
         * @type { number }
         */
        _this.stageId = stage_id || stageId;
        /**
         * The user profile associated with this claim
         * @type { HackerAPI.User }
         */
        _this.user = user !== undefined ? new User(user) : undefined;
        /**
         * The stage associated with this claim
         * @type { HackerAPI.Pipeline.Stage }
         */
        _this.stage = stage;
        /**
         * The collaborators associated with this claim
         * @type { HackerAPI.Claim.Collaborator[] }
         */
        _this.collaborators = (collaborators || []).map(function (c) { return new Collaborator(c); });
        /**
         * The reviews of the claim
         * @type { HackerAPI.Claim.Review[] }
         */
        _this.reviews = (reviews || []).map(function (r) { return new Review(r); });
        /**
         * The field answers of the claim. If this claim is being loaded from a get request then it will be a map
         * of FieldAnswers otherwise it will be a map of values.
         * @type { Object.<string, HackerAPI.Claim.FieldAnswer | any> }
         */
        _this.fields = fields;
        if (_this.fields) {
            for (var k in _this.fields) {
                _this.fields[k] =
                    typeof _this.fields[k] === "object" &&
                        _this.fields[k] !== undefined &&
                        _this.fields[k] !== null &&
                        (_this.fields[k]["field_id"] || _this.fields[k].fieldId)
                        ? new FieldAnswer(_this.fields[k])
                        : _this.fields[k];
            }
        }
        /**
         * When the claim was created
         * @type { Date }
         */
        _this.createdAt = parseDate(created_at || createdAt);
        /**
         * When the claim was last updated
         * @type { Date }
         */
        _this.updatedAt = parseDate(updated_at || updatedAt);
        return _this;
    }
    Object.defineProperty(Claim, "Collaborator", {
        /**
         * @returns
         */
        get: function () {
            return Collaborator;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Claim, "Review", {
        /**
         * @returns
         */
        get: function () {
            return Review;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Claim, "Filters", {
        /**
         * @returns
         */
        get: function () {
            return Filters;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Claim, "FieldAnswer", {
        /**
         * @returns
         */
        get: function () {
            return FieldAnswer;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Sets a field answer
     * @param fieldAnswer
     * @param [deepCopy = true] - Deep copy is safer because it should break earlier if something is fucked up
     */
    Claim.prototype.setFieldAnswer = function (fieldAnswer, deepCopy) {
        if (deepCopy === void 0) { deepCopy = true; }
        if (!this.fields) {
            this.fields = {};
        }
        this.fields[fieldAnswer.fieldId || fieldAnswer["field_id"]] = deepCopy
            ? new FieldAnswer(fieldAnswer)
            : fieldAnswer;
    };
    /**
     * Gets a claim using its ID from hackerapi
     * @param claim
     * @param claim.id
     * @param [auth]
     * @param [auth.token]
     * @returns
     */
    Claim.fetch = function (_a, token) {
        var id = _a.id;
        return HackerAPIFetch({
            path: "/v2/claims/" + id,
            method: "GET",
            token: token
        }).then(function (data) { return new Claim(data); });
    };
    /**
     * Gets all the claims belonging to a user
     * @param [user]
     * @param user.id
     * @param [auth]
     * @param [auth.token]
     * @param [pipeline]
     * @param pipeline.slug
     * @param [event]
     * @param event.slug
     * @returns
     */
    Claim.fetchAllByUserId = function (_a, pipeline, event, token) {
        var id = (_a === void 0 ? { id: "me" } : _a).id;
        var _b, _c;
        return HackerAPIFetch({
            path: "/v2/users/" + id + "/claims",
            method: "GET",
            token: token,
            params: {
                event: (_b = event) === null || _b === void 0 ? void 0 : _b.slug,
                pipeline: (_c = pipeline) === null || _c === void 0 ? void 0 : _c.slug
            }
        }).then(function (data) { return data.map(function (datum) { return new Claim(datum); }); });
    };
    /**
     * Returns the filtered claims for a pipeline
     * @param pipeline
     * @param pipeline.id
     * @param filters
     * @param user
     * @param token
     * @returns
     */
    Claim.fetchAllByPipeline = function (_a, filters, token) {
        var id = _a.id;
        var _b;
        return HackerAPIFetch({
            path: "/v2/pipelines/" + id + "/claims",
            method: "GET",
            params: (_b = filters) === null || _b === void 0 ? void 0 : _b.toHackerAPIFormat(),
            token: token
        }).then(function (data) { return data.map(function (datum) { return new Claim(datum); }); });
    };
    /**
     * Creates a new claim for a pipeline
     * @param pipeline
     * @param pipeline.id
     * @param claim
     * @param [auth]
     * @param [auth.token]
     * @returns
     */
    Claim.create = function (_a, claim, token) {
        var id = _a.id;
        return HackerAPIFetch({
            path: "/v2/pipelines/" + id + "/claims",
            method: "POST",
            token: token,
            body: claim.toHackerAPIFormat()
        }).then(function (data) { return new Claim(data); });
    };
    /**
     * Updates a claim on hackerapi
     * @param claim
     * @param [auth]
     * @param [auth.token]
     * @returns
     */
    Claim.update = function (claim, token) {
        return HackerAPIFetch({
            path: "/v2/claims/" + claim.id,
            method: "PUT",
            token: token,
            body: claim.toHackerAPIFormat()
        }).then(function (data) { return new Claim(data); });
    };
    /**
     * Updates a claim on hackerapi. This method is faster than Claim.update because the claim is not
     * returned in the response.
     * @param claim
     * @param [auth]
     * @param [auth.token]
     * @returns
     */
    Claim.updateNoResponse = function (claim, token) {
        return HackerAPIFetch({
            path: "/v2/claims/" + claim.id,
            method: "PATCH",
            token: token,
            body: claim.toHackerAPIFormat()
        });
    };
    /**
     * Deletes a claim using its ID from hackerapi
     * @param claim
     * @param claim.id
     * @param [auth]
     * @param [auth.token]
     * @returns
     */
    Claim.delete = function (_a, token) {
        var id = _a.id;
        return HackerAPIFetch({
            path: "/v2/claims/" + id,
            method: "DELETE",
            token: token
        });
    };
    return Claim;
}(Base));
export default Claim;
//# sourceMappingURL=claim.js.map